import { useCallback, useEffect } from 'react';

import { ActionKey, ActionSubjectIdKey, ActionSubjectKey } from '@/analytics/constants';
import { useFastshiftAnalyticsData } from '@/analytics/hooks/use-fastshift-analytics-data';
import { useFireAnalyticsEvents } from '@/analytics/use-fire-analytics-events';
import type { AssessmentRecommendationDetail } from '@/state/types';

type AssessmentRecommendationDetailsAnalyticsProps = {
	organizationId: string;
	recommendationId: string;
	detail: AssessmentRecommendationDetail | undefined;
};

export const useAssessmentRecommendationDetailsAnalytics = ({
	organizationId,
	recommendationId,
	detail,
}: AssessmentRecommendationDetailsAnalyticsProps) => {
	const { sourceKey, identityAttrs, uiIdentityAttrs } = useFastshiftAnalyticsData();
	const { fireTrackEvent } = useFireAnalyticsEvents(identityAttrs);
	const { fireUIEvent } = useFireAnalyticsEvents(uiIdentityAttrs);
	const displayedStatus =
		!detail || (detail.pending && !detail.data) || (!detail.error && !detail.data)
			? null
			: detail.data
				? detail.data.insights.length > 0
					? 'normal'
					: 'empty'
				: 'error';

	useEffect(() => {
		if (identityAttrs === null || displayedStatus === null) {
			return;
		}

		fireTrackEvent({
			action: ActionKey.DISPLAYED,
			actionSubject: ActionSubjectKey.SECTION,
			actionSubjectId: ActionSubjectIdKey.DC_INSTANCE_RECOMMENDATION_DETAILS,
			source: sourceKey,
			attributes: {
				selectedOrgId: organizationId,
				assessmentResultId: recommendationId,
				status: displayedStatus,
			},
		});
	}, [displayedStatus, fireTrackEvent, identityAttrs, organizationId, recommendationId, sourceKey]);

	const sendRecommendationDetailsDismissClickUIEvent = useCallback(() => {
		fireUIEvent({
			action: ActionKey.CLICKED,
			actionSubject: ActionSubjectKey.BUTTON,
			actionSubjectId: ActionSubjectIdKey.DC_INSTANCE_RECOMMENDATION_DETAILS_DISMISS_BUTTON,
			source: sourceKey,
			attributes: { selectedOrgId: organizationId, assessmentResultId: recommendationId },
		});
	}, [fireUIEvent, organizationId, recommendationId, sourceKey]);

	const sendRecommendationResourceLinkClickUIEvent = useCallback(() => {
		fireUIEvent({
			action: ActionKey.CLICKED,
			actionSubject: ActionSubjectKey.LINK,
			actionSubjectId: ActionSubjectIdKey.DC_INSTANCE_RECOMMENDATION_RESOURCE_LINK,
			source: sourceKey,
			attributes: { selectedOrgId: organizationId, assessmentResultId: recommendationId },
		});
	}, [fireUIEvent, organizationId, recommendationId, sourceKey]);

	return {
		sendRecommendationDetailsDismissClickUIEvent,
		sendRecommendationResourceLinkClickUIEvent,
	};
};
